import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/modules/auth/presentation/hooks/use-auth';
import { useTenant } from '@/modules/tenant/presentation/hooks/use-tenant';
import { submissionContainer } from '../../di/container';
import type { SubmissionKind } from '../../domain/entities/submission.entity';

function submissionKey(
  kind: SubmissionKind,
  userId: string | undefined,
  tenant: string,
  year: number,
) {
  return ['submission', kind, userId, tenant, year] as const;
}

/**
 * O envio desta pessoa nesta prova e edição — `null` se ainda não enviou.
 * Desligada sem sessão, e sem perfil nesta plataforma (o back responderia 403).
 */
export function useMySubmission(kind: SubmissionKind) {
  const { user } = useAuth();
  const { id, edition } = useTenant();

  return useQuery({
    queryKey: submissionKey(kind, user?.id, id, edition.year),
    queryFn: () =>
      submissionContainer.repository.getMine(kind, id, edition.year),
    enabled: user?.hasProfile === true,
  });
}

/**
 * Envia e já deixa a leitura pronta: a tela para onde a pessoa vai em seguida
 * (o painel) abre com o envio no cache, sem esperar outra ida à rede.
 */
export function useSubmit(kind: SubmissionKind) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { id, edition } = useTenant();

  return useMutation({
    mutationFn: (answers: Record<string, number>) =>
      submissionContainer.repository.submit(kind, {
        exam: id,
        edition: edition.year,
        answers,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: submissionKey(kind, user?.id, id, edition.year),
      }),
  });
}
